import { useEffect, useState } from "react";
import { PageHeader, Empty } from "@/components/UIBits";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { Flame } from "lucide-react";

export default function Mappings() {
  const [rows, setRows] = useState([]);

  const load = async () => {
    try {
      setRows(await api.listMappings());
    } catch (e) {
      toast.error("Failed to load mappings");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const active = rows.filter((r) => r.status === "active");

  const burn = async (id, stranger) => {
    try {
      await api.burnMapping(id);
      toast.success(`Burned ${stranger}`);
      load();
    } catch (e) {
      toast.error("Burn failed");
    }
  };

  return (
    <div data-testid="mappings-page">
      <PageHeader
        title="Shadow Mappings"
        subtitle={`// ${active.length} active routes`}
        testid="mappings-header"
      />

      {rows.length === 0 ? (
        <Empty testid="mappings-empty">
          No mappings yet. Send a simulated inbound to create one.
        </Empty>
      ) : (
        <div className="border border-[#1f1f23] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#1f1f23] bg-[#0e0e10]">
                <Th>User</Th>
                <Th>Real Number</Th>
                <Th>Shadow #</Th>
                <Th>Stranger</Th>
                <Th>Msgs</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th align="right">Action</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-[#18181b] hover:bg-[#0e0e10]"
                  data-testid={`mapping-row-${r.id}`}
                >
                  <Td>
                    <span className="text-white">{r.user_name || "—"}</span>
                  </Td>
                  <Td mono>{r.user_real_number}</Td>
                  <Td mono className="text-amber-400">{r.shadow_number}</Td>
                  <Td mono className="text-blue-400">{r.stranger_number}</Td>
                  <Td mono>{r.message_count || 0}</Td>
                  <Td>
                    <span
                      className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border ${
                        r.status === "active"
                          ? "border-emerald-700 text-emerald-400"
                          : "border-red-800 text-red-400"
                      }`}
                    >
                      {r.status}
                    </span>
                  </Td>
                  <Td mono className="text-zinc-500 text-xs">
                    {new Date(r.created_at).toLocaleString()}
                  </Td>
                  <Td align="right">
                    {r.status === "active" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid={`burn-btn-${r.id}`}
                            className="bg-transparent border-red-900 text-red-400 hover:bg-red-950 hover:text-red-300"
                          >
                            <Flame className="w-3 h-3 mr-1" /> BURN
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#0e0e10] border-[#27272a]">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-heading">
                              Burn this contact?
                            </AlertDialogTitle>
                            <AlertDialogDescription className="font-mono text-xs text-zinc-400">
                              Stranger {r.stranger_number} will be permanently
                              blocked for {r.user_real_number}. Shadow{" "}
                              {r.shadow_number} will be freed.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid={`burn-cancel-${r.id}`}>
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              data-testid={`burn-confirm-${r.id}`}
                              className="bg-red-600 hover:bg-red-700"
                              onClick={() => burn(r.id, r.stranger_number)}
                            >
                              Burn
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th
      className={`text-${align} font-mono text-[10px] uppercase tracking-widest text-zinc-500 px-4 py-3`}
    >
      {children}
    </th>
  );
}

function Td({ children, mono, align = "left", className = "" }) {
  return (
    <td
      className={`px-4 py-3 text-${align} ${
        mono ? "font-mono text-sm" : ""
      } ${className}`}
    >
      {children}
    </td>
  );
}
